import type {
  MapInfo, MapPrivacyLevel,
} from '~/_shared/types/map';
import { type TranslationFnc } from '~/_shared/utils/hooks';
import { notNullsy } from '~/_shared/utils/typeGuards';
import type { MapListingItem } from '~/map/listing/item/mapListingItem';
import type { MapListingRowItem } from '~/map/listing/tableData/mapRow/mapRow.component';

export enum MapListSortType {
  Name_ASC = 'name_asc',
  Name_DESC = 'name_desc',
  Updated_DESC = 'updated_at_desc',
  Layered = 'layered',
  Created_DESC = 'created_at_desc',
  Created_ASC = 'created_at_asc',
  OnlyMine = 'only_mine',
  SharedWithMe = 'shared_with_me',
}

const MapListSortTypesFilters = [
  MapListSortType.Layered,
  MapListSortType.OnlyMine,
  MapListSortType.SharedWithMe,
];

export const getSortOptions = (t: TranslationFnc) => ([{
  name: t('All Maps A to Z'),
  value: MapListSortType.Name_ASC,
}, {
  name: t('All Maps Z to A'),
  value: MapListSortType.Name_DESC,
}, {
  name: t('Most Recently Updated Maps'),
  value: MapListSortType.Updated_DESC,
}, {
  name: t('All Layered Maps'),
  value: MapListSortType.Layered,
}, {
  name: t('Newest to Oldest Maps'),
  value: MapListSortType.Created_DESC,
}, {
  name: t('Oldest to Newest Maps'),
  value: MapListSortType.Created_ASC,
}, {
  name: t('mapListSortTypes.onlyMine'),
  value: MapListSortType.OnlyMine,
}, {
  name: t('mapListSortTypes.onlySharedWithMe'),
  value: MapListSortType.SharedWithMe,
},
]);

export const isMapListSortTypeForSorting = (sortType: MapListSortType) => {
  return !MapListSortTypesFilters.includes(sortType);
};

export const checkMapCanBeShared = (mapListingItem: MapListingRowItem | MapInfo) => {
  return mapListingItem.source !== 'salesforce' && !mapListingItem.isReadOnly;
};

export const isInfoOfBaseLayer = (mapInfo: MapInfo) => {
  return mapInfo.parentMap?.layering?.baseMaps.find(map => map.id === mapInfo.id);
};

export const updateBaseMapPrivacy = ({ baseMapId, parentMap, privacyLevel }: {
  baseMapId: number;
  parentMap?: MapListingItem;
  privacyLevel: MapPrivacyLevel;
}): MapListingItem | null => {
  const layering = parentMap?.layering;
  if (!layering || !parentMap.isLayered) {
    return null;
  }

  return {
    ...parentMap,
    layering: {
      ...layering,
      baseMaps: layering.baseMaps.map((baseMap) => {
        if (baseMap.id === baseMapId) {
          return { ...baseMap, privacy: privacyLevel };
        }

        return baseMap;
      }),
    },
  };
};

export const getMapRowFlags = (
  item: MapListingRowItem,
  parentMapItem?: MapListingRowItem | MapListingItem,
) => {
  const isBaseLayer = !!item.isBaseLayer;
  const isSnapshot = notNullsy(parentMapItem) && !isBaseLayer;
  const isSubItem = isBaseLayer || isSnapshot;
  const canMapBeShared = checkMapCanBeShared(item);
  const userHasAccess = !item.noAccess;

  return { isBaseLayer, isSnapshot, isSubItem, canMapBeShared, userHasAccess };
};

export const getSubMapsCount = (item: MapListingRowItem) => {
  const snapshotCount = item.snapshots.length;
  const baseMapsCount = (item.layering?.connected && item.layering?.baseMaps.length) || 0;

  return snapshotCount + baseMapsCount;
};
